// @systeme: questionnaires
// ============================================================================
// PairsForm - Page Réponse Questionnaire Qualiopi
// ============================================================================
// Interface publique (sans authentification) pour répondre aux questionnaires
// Qualiopi (attentes, satisfaction, apprentissages). Le participant reçoit
// un lien unique avec un token.
//
// URL: /questionnaire/repondre/:token
// ============================================================================

import { CheckCircle, Clock, Send, User } from "lucide-react";
import React, { useState, useEffect } from "react";
import { API_URL, messageErreur } from "../lib/api";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  Paper,
  Container,
  Checkbox,
  FormGroup,
  Rating,
  Divider,
} from "@mui/material";
import { toast } from "sonner";
import EchelleReponse from "../components/questionnaire/EchelleReponse";
import type { QuestionOptions } from "../components/questionnaire/types";
import { formatDateShortFR, formatDateJourFR } from "@cecodev/shared";

// ============================================================================
// Types
// ============================================================================

interface Question {
  id: string;
  code: string;
  texte: string;
  texteAide?: string;
  typeReponse: "ECHELLE" | "NOTE" | "TEXTE_COURT" | "TEXTE_LONG" | "QCM" | "QCM_MULTIPLE" | "OUI_NON";
  obligatoire: boolean;
  // Même source de type que l'éditeur : une seule déclaration pour un seul
  // champ JSON (cf. `components/questionnaire/types.ts`).
  options?: QuestionOptions;
  section?: string;
  ordre: number;
}

interface Questionnaire {
  id: string;
  nom: string;
  description?: string;
  type: string;
  estAnonyme: boolean;
  dureeEstimeeMinutes?: number;
  introduction?: string;
  conclusion?: string;
}

interface Participant {
  prenom: string;
  nom: string;
}

interface QuestionnaireData {
  reponseId: string;
  questionnaire: Questionnaire;
  questions: Question[];
  participant: Participant | null;
  dateFermeture?: string;
  /** La personne a déjà rendu ce questionnaire (relecture). */
  dejaRepondu?: boolean;
  dateFin?: string | null;
  /** La campagne est-elle encore ouverte ? (autorise la correction) */
  modifiable?: boolean;
  reponsesEnregistrees?: Record<string, string | string[] | number> | null;
}

// ============================================================================
// Component
// ============================================================================

export default function QuestionnaireRepondrePage() {
  const { token } = useParams<{ token: string }>();

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QuestionnaireData | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [reponses, setReponses] = useState<Record<string, string | string[] | number>>({});
  const [draftRestored, setDraftRestored] = useState(false);
  // Relecture : la personne a déjà répondu. On affiche ses réponses, et on
  // n'ouvre la correction que si la campagne est encore ouverte.
  const [enCorrection, setEnCorrection] = useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    if (!token) {
      setError("Lien invalide : token manquant");
      setLoading(false);
      return;
    }

    loadQuestionnaire();
  }, [token]);

  // ============================================================================
  // Data Loading
  // ============================================================================

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/questionnaires-public/${token}`);
      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "Erreur lors du chargement");
        return;
      }

      setData(result);
      // Pré-remplissage avec ce qui a été enregistré (relecture / correction).
      if (result?.dejaRepondu && result?.reponsesEnregistrees) {
        setReponses(result.reponsesEnregistrees as Record<string, string | string[] | number>);
      }
    } catch (err) {
      console.error("Erreur chargement questionnaire:", err);
      setError("Impossible de charger le questionnaire");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // localStorage draft persistence
  // ============================================================================

  const DRAFT_KEY = `questionnaire_draft_${token}`;

  // Restaurer le brouillon au chargement
  useEffect(() => {
    if (!token) return;
    // Un questionnaire déjà rendu fait foi : son contenu enregistré prime sur
    // un brouillon local resté d'une session précédente.
    if (data?.dejaRepondu) return;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
          setReponses(parsed);
          setDraftRestored(true);
        }
      }
    } catch {
      // Brouillon corrompu, on ignore
    }
  }, [token, data?.dejaRepondu]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleReponse = (questionId: string, valeur: string | string[] | number) => {
    // Relecture : rien ne bouge tant que la personne n'a pas cliqué
    // « Modifier mes réponses ».
    if (data?.dejaRepondu && !enCorrection) return;
    setReponses((prev) => {
      const updated = { ...prev, [questionId]: valeur };
      // Sauvegarder automatiquement dans localStorage
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(updated));
      } catch {
        // Quota dépassé, on ignore
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!data || !token) return;

    // Vérifier les questions obligatoires
    const questionsObligatoires = data.questions.filter((q) => q.obligatoire);
    const questionsNonRepondues = questionsObligatoires.filter((q) => {
      const reponse = reponses[q.id];
      if (reponse === undefined || reponse === null) return true;
      if (typeof reponse === "string" && reponse.trim() === "") return true;
      if (Array.isArray(reponse) && reponse.length === 0) return true;
      return false;
    });

    if (questionsNonRepondues.length > 0) {
      toast.error(`Veuillez répondre à toutes les questions obligatoires (${questionsNonRepondues.length} manquante(s))`);
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API_URL}/questionnaires-public/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reponses }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erreur lors de l'envoi");
      }

      setSubmitted(true);
      // Nettoyer le brouillon après soumission réussie
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      toast.success("Merci ! Vos réponses ont été enregistrées.");
    } catch (err) {
      console.error("Erreur soumission:", err);
      toast.error(messageErreur(err) || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // Helpers
  // ============================================================================

  const getSections = (): { name: string | null; questions: Question[] }[] => {
    if (!data) return [];

    const sectionsMap = new Map<string | null, Question[]>();

    data.questions.forEach((q) => {
      const section = q.section || null;
      if (!sectionsMap.has(section)) {
        sectionsMap.set(section, []);
      }
      sectionsMap.get(section)!.push(q);
    });

    return Array.from(sectionsMap.entries()).map(([name, questions]) => ({
      name,
      questions,
    }));
  };

  const getProgressPercentage = () => {
    if (!data) return 0;
    const totalObligatoires = data.questions.filter((q) => q.obligatoire).length;
    if (totalObligatoires === 0) return 100;

    const repondues = data.questions.filter((q) => {
      if (!q.obligatoire) return false;
      const r = reponses[q.id];
      if (r === undefined || r === null) return false;
      if (typeof r === "string" && r.trim() === "") return false;
      if (Array.isArray(r) && r.length === 0) return false;
      return true;
    }).length;

    return (repondues / totalObligatoires) * 100;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ATTENTES: "Questionnaire d'attentes",
      SATISFACTION_SEANCE: "Questionnaire de satisfaction (séance)",
      SATISFACTION_GLOBALE: "Questionnaire de satisfaction (formation)",
      APPRENTISSAGES: "Questionnaire d'apprentissages",
    };
    return labels[type] || type;
  };

  // ============================================================================
  // Render Question
  // ============================================================================

  const renderQuestion = (question: Question) => {
    const { id, texte, texteAide, typeReponse, obligatoire, options } = question;
    const value = reponses[id];

    return (
      <Card key={id} sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel
              component="legend"
              sx={{
                mb: 2,
                color: "#334155",
                fontWeight: 500,
                fontSize: "1rem",
                "&.Mui-focused": { color: "#334155" },
              }}
            >
              {texte}
              {obligatoire && (
                <Typography component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                  *
                </Typography>
              )}
            </FormLabel>

            {texteAide && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
                {texteAide}
              </Typography>
            )}

            {/* ECHELLE — rendu PARTAGÉ avec l'aperçu de l'éditeur
                (`<EchelleReponse>`) : l'éditeur annonçait des étoiles et le
                répondant recevait une pile de radios. Un aperçu qui ment sur le
                rendu final est pire qu'une absence d'aperçu. */}
            {typeReponse === "ECHELLE" && (
              <EchelleReponse
                options={options}
                value={value as string | number | null}
                onChange={(v) => handleReponse(id, v)}
              />
            )}

            {/* NOTE */}
            {typeReponse === "NOTE" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Rating
                  value={typeof value === "number" ? value : parseInt(value as string) || 0}
                  max={options?.max || 5}
                  onChange={(_, newValue) => handleReponse(id, newValue || 0)}
                  sx={{
                    "& .MuiRating-iconFilled": { color: "#4BB7A5" },
                    "& .MuiRating-iconHover": { color: "#3a9a87" },
                    fontSize: "2rem",
                  }}
                />
                <Typography color="text.secondary">
                  {value ? `${value} / ${options?.max || 5}` : "Non noté"}
                </Typography>
              </Box>
            )}

            {/* OUI_NON */}
            {typeReponse === "OUI_NON" && (
              <RadioGroup
                row
                value={value ?? ""}
                onChange={(e) => handleReponse(id, e.target.value)}
              >
                <FormControlLabel
                  value="oui"
                  control={<Radio sx={{ color: "#4BB7A5", "&.Mui-checked": { color: "#4BB7A5" } }} />}
                  label="Oui"
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    mr: 2,
                    ...(value === "oui" && {
                      border: "2px solid #4BB7A5",
                      bgcolor: "#f0fdf9",
                    }),
                  }}
                />
                <FormControlLabel
                  value="non"
                  control={<Radio sx={{ color: "#4BB7A5", "&.Mui-checked": { color: "#4BB7A5" } }} />}
                  label="Non"
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    ...(value === "non" && {
                      border: "2px solid #4BB7A5",
                      bgcolor: "#f0fdf9",
                    }),
                  }}
                />
              </RadioGroup>
            )}

            {/* QCM (choix unique) */}
            {typeReponse === "QCM" && options?.choix && (
              <RadioGroup
                value={value ?? ""}
                onChange={(e) => handleReponse(id, e.target.value)}
              >
                {options.choix.map((choix, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={choix}
                    control={<Radio sx={{ color: "#4BB7A5", "&.Mui-checked": { color: "#4BB7A5" } }} />}
                    label={choix}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 1,
                      mb: 1,
                      p: 1,
                      mr: 0,
                      "&:hover": { bgcolor: "#f8fafc" },
                      ...(value === choix && {
                        border: "2px solid #4BB7A5",
                        bgcolor: "#f0fdf9",
                      }),
                    }}
                  />
                ))}
              </RadioGroup>
            )}

            {/* QCM_MULTIPLE (choix multiples) */}
            {typeReponse === "QCM_MULTIPLE" && options?.choix && (
              <FormGroup>
                {options.choix.map((choix, idx) => {
                  const selected = Array.isArray(value) && value.includes(choix);
                  return (
                    <FormControlLabel
                      key={idx}
                      control={
                        <Checkbox
                          checked={selected}
                          onChange={(e) => {
                            const current = Array.isArray(value) ? value : [];
                            if (e.target.checked) {
                              handleReponse(id, [...current, choix]);
                            } else {
                              handleReponse(id, current.filter((c) => c !== choix));
                            }
                          }}
                          sx={{ color: "#4BB7A5", "&.Mui-checked": { color: "#4BB7A5" } }}
                        />
                      }
                      label={choix}
                      sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 1,
                        mb: 1,
                        p: 1,
                        mr: 0,
                        "&:hover": { bgcolor: "#f8fafc" },
                        ...(selected && {
                          border: "2px solid #4BB7A5",
                          bgcolor: "#f0fdf9",
                        }),
                      }}
                    />
                  );
                })}
              </FormGroup>
            )}

            {/* TEXTE_COURT */}
            {typeReponse === "TEXTE_COURT" && (
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Votre réponse..."
                value={value ?? ""}
                onChange={(e) => handleReponse(id, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: "#4BB7A5" },
                    "&.Mui-focused fieldset": { borderColor: "#4BB7A5" },
                  },
                }}
              />
            )}

            {/* TEXTE_LONG */}
            {typeReponse === "TEXTE_LONG" && (
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Votre réponse..."
                value={value ?? ""}
                onChange={(e) => handleReponse(id, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": { borderColor: "#4BB7A5" },
                    "&.Mui-focused fieldset": { borderColor: "#4BB7A5" },
                  },
                }}
              />
            )}
          </FormControl>
        </CardContent>
      </Card>
    );
  };

  // ============================================================================
  // Render: Loading
  // ============================================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 300, px: 2, textAlign: "center" }}>
          <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#4BB7A5" } }} />
          <Typography color="text.secondary">Chargement du questionnaire...</Typography>
        </Box>
      </Box>
    );
  }

  // ============================================================================
  // Render: Error
  // ============================================================================

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Questionnaire non accessible
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  // ============================================================================
  // Render: Success
  // ============================================================================

  if (submitted && data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 600, textAlign: "center" }}>
          <CardContent sx={{ p: 6 }}>
            <CheckCircle size={100} color="#4BB7A5" style={{ marginBottom: 24 }} />
            <Typography variant="h4" sx={{ mb: 2, color: "#334155" }}>
              Merci pour votre participation !
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {data.questionnaire.conclusion ||
                "Vos réponses ont été enregistrées. Elles nous aideront à améliorer la qualité de nos formations."}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              Vous pouvez maintenant fermer cette page.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ============================================================================
  // Render: Main Questionnaire
  // ============================================================================

  if (!data) return null;

  const { questionnaire, questions, participant, dateFermeture } = data;
  const sections = getSections();
  const progressPercentage = getProgressPercentage();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h3" sx={{ mb: 1, color: "#4BB7A5", fontWeight: "bold" }}>
            PairsForm
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            l'app qui catalyse l'intelligence collective
          </Typography>
        </Box>

        {/* Questionnaire Info */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            {getTypeLabel(questionnaire.type)}
          </Typography>
          <Typography variant="h5" sx={{ mb: 1, color: "#334155" }}>
            {questionnaire.nom}
          </Typography>
          {questionnaire.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {questionnaire.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {questionnaire.dureeEstimeeMinutes && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "#e2e8f0", px: 2, py: 0.5, borderRadius: 1 }}>
                <Clock size={16} />
                <Typography variant="caption">~{questionnaire.dureeEstimeeMinutes} min</Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ bgcolor: "#e2e8f0", px: 2, py: 0.5, borderRadius: 1 }}>
              {questions.length} questions
            </Typography>
            {questionnaire.estAnonyme && (
              <Typography variant="caption" sx={{ bgcolor: "#dbeafe", color: "#1e40af", px: 2, py: 0.5, borderRadius: 1 }}>
                Anonyme
              </Typography>
            )}
          </Box>

          {/* Participant info (si non anonyme) */}
          {!questionnaire.estAnonyme && participant && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f1f5f9", borderRadius: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <User size={18} color="#64748b" />
              <Typography variant="body2">
                Questionnaire pour <strong>{participant.prenom} {participant.nom}</strong>
              </Typography>
            </Box>
          )}

          {/* Date limite */}
          {dateFermeture && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Date limite de réponse : {formatDateJourFR(dateFermeture)}
            </Alert>
          )}
        </Paper>

        {/* Relecture d'un questionnaire déjà rendu (FX 2026-08-21). */}
        {data.dejaRepondu && !enCorrection && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              data.modifiable ? (
                <Button size="small" onClick={() => setEnCorrection(true)} sx={{ textTransform: "none" }}>
                  Modifier mes réponses
                </Button>
              ) : undefined
            }
          >
            Vous avez répondu à ce questionnaire
            {data.dateFin ? ` le ${formatDateShortFR(data.dateFin)}` : ""}.
            Vos réponses sont affichées ci-dessous.
            {!data.modifiable && " La période de réponse est close : elles ne sont plus modifiables."}
          </Alert>
        )}

        {/* Brouillon restauré */}
        {draftRestored && !submitted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Vos réponses précédentes ont été restaurées automatiquement. Vous pouvez continuer où vous vous étiez arrêté(e).
          </Alert>
        )}

        {/* Introduction */}
        {questionnaire.introduction && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {questionnaire.introduction}
          </Alert>
        )}

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progression
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progressPercentage.toFixed(0)}% complété
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: "#e2e8f0",
              "& .MuiLinearProgress-bar": { bgcolor: "#4BB7A5" },
            }}
          />
        </Box>

        {/* Questions by Section */}
        {sections.map((section, sectionIdx) => (
          <Box key={sectionIdx} sx={{ mb: 4 }}>
            {section.name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: "#334155", fontWeight: 600 }}>
                  {section.name}
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Box>
            )}
            {section.questions.map(renderQuestion)}
          </Box>
        ))}

        {/* Submit Button — masqué en simple RELECTURE : on ne propose « Envoyer »
            que quand il y a quelque chose à envoyer. */}
        {(!data.dejaRepondu || enCorrection) && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? null : <Send size={18} />}
              sx={{
                bgcolor: "#4BB7A5",
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                "&:hover": { bgcolor: "#3a9a87" },
                "&:disabled": { bgcolor: "#94a3b8" },
              }}
            >
              {submitting
                ? "Envoi en cours..."
                : enCorrection
                  ? "Enregistrer mes corrections"
                  : "Envoyer mes réponses"}
            </Button>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="caption" color="text.secondary">
            {questionnaire.estAnonyme
              ? "Vos réponses sont anonymes et confidentielles"
              : "Vos réponses sont confidentielles"}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
